import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { CalloutView } from './CalloutView';

export const CALLOUT_TONES = ['info', 'warning', 'success', 'danger'] as const;
export type CalloutTone = (typeof CALLOUT_TONES)[number];

export const DEFAULT_CALLOUT_TONE: CalloutTone = 'info';

/** Anything unrecognised (an older document, a hand-edited attribute) falls back
 *  to `info` rather than rendering an unstyled box. */
export function normalizeCalloutTone(value: unknown): CalloutTone {
    return (CALLOUT_TONES as readonly string[]).includes(value as string)
        ? (value as CalloutTone)
        : DEFAULT_CALLOUT_TONE;
}

/**
 * Notice box holding one or more paragraphs, tinted by `tone`. Registered in the
 * schema unconditionally so a stored document renders read-only everywhere; the
 * toolbar affordance is what callers gate (RichEditorProps.allowCallouts).
 */
export const Callout = Node.create({
    name: 'callout',
    group: 'block',
    content: 'paragraph+',
    defining: true,

    addAttributes() {
        return {
            tone: {
                default: DEFAULT_CALLOUT_TONE,
                parseHTML: (element) => normalizeCalloutTone(element.getAttribute('data-tone')),
                renderHTML: (attributes) => ({
                    'data-tone': normalizeCalloutTone((attributes as { tone?: unknown }).tone)
                })
            }
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-callout]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-callout': '' }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(CalloutView);
    }
});
